<template>
  <div class="instruction-panel-entry mt-2">
    <h2 class="h2">{{ $t("instruction-panel.echanges.title") }}</h2>

    <ValidationObserver
      v-if="isNational || isRegional"
      ref="commentObserver"
      v-slot="{ handleSubmit }"
    >
      <div class="columns mt-5">
        <div class="column is-one-third">
          <div class="columns">
            <div class="column pb-0">
              {{ loggedUser ? loggedUser.prenom + " " + loggedUser.nom : "" }}
            </div>
          </div>
          <div class="columns">
            <div class="column pb-0 today date-style">{{ formatDate(new Date()) }}</div>
            <div class="column pt-4 pb-0">
              <b-icon icon="comment-alt" size="is-small" type="is-info"></b-icon>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="columns">
            <div class="column pt-0 pb-0">
              <InputWithValidation
                v-model="contenu"
                rules="required"
                type="textarea"
                vid="comment"
                maxlength="1400"
              />
            </div>
          </div>
          <div class="columns">
            <div class="column pt-0 pb-0">
              <b-button type="is-info is-outlined" @click="handleSubmit(addComment)"
                >{{ $t("instruction-panel.echanges.poster") }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>

    <div class="mt-5">
      <div v-for="echange in echanges.elements" :key="echange.id" class="columns mt-1 mb-0">
        <div class="column is-one-third">
          <div class="columns">
            <div v-if="echange.auteur.nom !== null" class="column pb-0">
              {{ echange.auteur.prenom + " " + echange.auteur.nom }}
            </div>
            <div v-else class="column pb-0">
              {{ $t("instruction-panel.echanges.unknown-user") }}
            </div>
          </div>
          <div class="columns">
            <div class="column pb-0 today date-style">
              {{ formatDate(echange.dateCreation) }}
            </div>
            <div class="column pt-4 pb-0">
              <b-icon
                v-if="echange.type === 'COMMENTAIRE'"
                icon="comment-alt"
                size="is-small"
                type="is-info"
              ></b-icon>
              <b-icon
                v-if="echange.type === 'COURRIEL'"
                icon="envelope"
                size="is-small"
                type="is-info"
              ></b-icon>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="columns">
            <div class="column pb-0">
              <div class="textarea-container">
                <div
                  v-if="echange.type === 'COMMENTAIRE'"
                  class="box message"
                  @click="openComment(echange)"
                >
                  {{ echange.contenu }}
                </div>
                <div
                  v-if="echange.type === 'COURRIEL'"
                  class="box courriel-title"
                  @click="openCourriel(echange)"
                >
                  {{ echange.titre }}
                </div>
                <div v-if="echange.type === 'COMMENTAIRE'" class="actions">
                  <div class="columns">
                    <div class="column p-0 m-0">
                      <a @click="openComment(echange)">
                        <b-icon icon="angle-down" size="is-small" type="is-dark"></b-icon>
                      </a>
                    </div>
                  </div>
                  <div class="columns">
                    <div class="column p-0 m-0">
                      <a v-if="isNational || isRegional" @click="removeComment(echange)">
                        <b-icon icon="trash" size="is-small" type="is-danger"></b-icon>
                      </a>
                    </div>
                  </div>
                </div>
                <div v-if="echange.type === 'COURRIEL'" class="courriel-actions">
                  <div class="columns">
                    <div class="column p-0 m-0">
                      <a v-if="echange.type === 'COURRIEL'" @click="openCourriel(echange)">
                        <b-icon icon="eye" size="is-small" type="is-dark"></b-icon>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="columns mt-2">
      <div class="column"></div>
      <div class="column is-2">
        <b-button
          icon-left="angle-left"
          :disabled="pagination.pageNumber === 1"
          @click="loadPreviousPage()"
        ></b-button>
      </div>
      <div class="column is-2">
        <b-button
          icon-left="angle-right"
          :disabled="canLoadNextPage"
          @click="loadNextPage()"
        ></b-button>
      </div>
    </div>

    <CommentaireModalView
      :commentaire="commentaireSelected"
      :open="openCommentaireModal"
      @close="openCommentaireModal = false"
    />
    <CourrielModalView
      :courriel="courrielSelected"
      :open="openCourrielModal"
      @close="openCourrielModal = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { BButton, BIcon } from "buefy"
import {
  CommentaireCreateDTO,
  CommentaireDTO,
  EchangeDTO,
  PaginationParameter,
  PaginationResult,
  ProjetAnnuelDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { LoginService } from "@/services/rest/login/LoginService"
import { UsersService } from "@/services/rest/users/UsersService"
import { CommentaireService } from "@/services/rest/commentaire/CommentaireService"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import DateUtils from "@/services/DateUtils"
import CommentaireModalView from "./CommentaireModalView.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import CourrielModalView from "./CourrielModalView.vue"

export default defineComponent({
  name: "InstructionPanelEchanges",
  components: {
    InputWithValidation,
    ValidationObserver,
    CommentaireModalView,
    CourrielModalView,
    BButton,
    BIcon,
  },
  props: {
    project: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      commentaireService: CommentaireService.INSTANCE,
      usersService: UsersService.INSTANCE,
      loggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      isRegional: false,
      isNational: false,
      contenu: "",
      echanges: new PaginationResult() as PaginationResult<EchangeDTO>,
      pagination: { ...new PaginationParameter(), pageNumber: 1, pageSize: 3 },
      canLoadNextPage: false,
      openCommentaireModal: false,
      commentaireSelected: new EchangeDTO(),
      openCourrielModal: false,
      courrielSelected: new EchangeDTO(),
    }
  },
  created(): void {
    this.loggedUser = this.loginService.getLoggedUser()
    if (!this.loggedUser || !this.loggedUser.id) {
      return
    }

    this.isNational = this.usersService.hasAnyProfile(this.loggedUser, [
      PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
      PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
    ])

    this.isRegional =
      this.usersService.hasAnyProfile(this.loggedUser, [
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
        PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
      ]) &&
      this.loggedUser.grae !== undefined &&
      this.project.grae != undefined &&
      this.loggedUser.grae.id === this.project.grae.id
  },
  mounted(): void {
    if (this.project && this.project.projetId) {
      this.loadEchanges(this.project.projetId)
    }
  },
  methods: {
    async loadEchanges(idProjet: string): Promise<void> {
      this.echanges = await this.commentaireService.getEchanges(idProjet, this.pagination)
      this.computeCanLoadNextPage()
    },
    formatDate(date: Date): string {
      return DateUtils.formatDate(date)
    },
    async addComment(): Promise<void> {
      if (this.project && this.project.projetId) {
        const comment = {
          contenu: this.contenu,
        } as CommentaireCreateDTO
        this.commentaireService.createComment(this.project.projetId, comment).then(() => {
          this.contenu = ""
          requestAnimationFrame(() => {
            ;(this.$refs.commentObserver as any).reset()
          })
          this.loadEchanges(this.project.projetId)
        })
      }
    },
    openComment(comment: EchangeDTO): void {
      this.commentaireSelected = comment
      this.openCommentaireModal = true
    },
    openCourriel(courriel: EchangeDTO): void {
      this.courrielSelected = courriel
      this.openCourrielModal = true
    },
    async removeComment(comment: CommentaireDTO): Promise<void> {
      this.$buefy.dialog.confirm({
        message: this.$t("instruction-panel.echanges.remove-comment").toString(),
        confirmText: this.$t("confirmer").toString(),
        cancelText: this.$t("annuler").toString(),
        onConfirm: () =>
          this.commentaireService.removeComment(comment.projet, comment.id).then(() => {
            this.loadEchanges(this.project.projetId)
          }),
      })
    },
    computeCanLoadNextPage(): void {
      this.canLoadNextPage =
        this.echanges.count / this.pagination.pageSize <= this.pagination.pageNumber
    },
    async loadNextPage(): Promise<void> {
      this.pagination.pageNumber++
      await this.loadEchanges(this.project.projetId)
    },
    async loadPreviousPage(): Promise<void> {
      this.pagination.pageNumber--
      await this.loadEchanges(this.project.projetId)
    },
  },
})
</script>

<style lang="scss">
.date-style {
  font-style: italic;
  color: grey;
}

.textarea-container {
  position: relative;
}

.textarea-container .box {
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  text-align: left;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  border: solid 1px grey;
  border-radius: 5px;
  background-color: white;
  padding: 5px 25px 5px 5px;
  margin-bottom: 0 !important;
}

.textarea-container .message {
  height: 105px;
  -webkit-line-clamp: 4;
  line-clamp: 4;
}

.textarea-container .courriel-title {
  height: 60px;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.textarea-container .actions {
  position: absolute;
  top: 45px;
  right: 7px;
}

.textarea-container .courriel-actions {
  position: absolute;
  top: 35px;
  right: 7px;
}

.comment-style {
  width: 100%;
  resize: none;
  min-height: 76px;
}
</style>
